import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, Clock, Star, Scissors, Sparkles, Heart, Users, CheckCircle2, ArrowRight } from 'lucide-react';
import { SALON, SERVICES_OVERVIEW, TESTIMONIALS, WHY_CHOOSE, IMAGES, whatsappLink, telLink } from '../data/content';

const ICONS = { Scissors, Sparkles, Heart, Users };

const Home = () => {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="Headtouch Salon interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/80" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 py-20">
          <div className="max-w-3xl">
            <span className="inline-block animate-fade-in text-gold font-medium text-sm md:text-base tracking-[0.25em] uppercase mb-5">
              A Family Salon
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] animate-fade-in-up delay-100">
              Headtouch &ndash; A Family Salon in <span className="text-gold-gradient">Dhakoli</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-neutral-200 max-w-2xl leading-relaxed animate-fade-in-up delay-200">
              Premium Hair, Skin & Grooming Services for Men & Women
            </p>

            {/* Info chips */}
            <div className="mt-7 flex flex-wrap gap-3 animate-fade-in-up delay-300">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                <MapPin className="w-4 h-4 text-gold" />
                <span className="text-sm text-white">{SALON.city}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                <Clock className="w-4 h-4 text-gold" />
                <span className="text-sm text-white">{SALON.timing}</span>
              </div>
            </div>

            {/* Rating row */}
            <div className="mt-8 flex flex-wrap items-center gap-6 text-white animate-fade-in-up delay-400">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-gold text-gold" />
                <span className="font-semibold">5.0 Rating</span>
              </div>
              <div className="w-px h-5 bg-white/30" />
              <span className="text-sm">20+ Happy Clients</span>
              <div className="w-px h-5 bg-white/30 hidden sm:block" />
              <span className="text-sm hidden sm:block">Friendly Staff</span>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-500">
              <a
                href={telLink}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-gold text-white rounded-full font-semibold shadow-lg hover:bg-gold-dark btn-transition hover:shadow-xl hover:-translate-y-0.5"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-whatsapp text-white rounded-full font-semibold shadow-lg hover:bg-whatsapp-dark btn-transition hover:shadow-xl hover:-translate-y-0.5"
              >
                <MessageCircle className="w-5 h-5 fill-white" />
                Book on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-gold font-medium text-sm tracking-[0.25em] uppercase">Our Expertise</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold mt-3 mb-5 text-neutral-900">Our Services</h2>
            <div className="gold-divider mx-auto mb-6" />
            <p className="text-neutral-600 leading-relaxed">
              We offer a wide range of professional salon services for the entire family
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {SERVICES_OVERVIEW.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <div key={s.title} className="service-card bg-white border border-gold/15 rounded-2xl p-7 text-center">
                  <div className="w-14 h-14 mx-auto rounded-xl bg-gold-soft flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-gold" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-neutral-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-gold text-gold rounded-full font-medium btn-transition hover:bg-gold hover:text-white"
            >
              View All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-gold font-medium text-sm tracking-[0.25em] uppercase">Why Choose Us</span>
              <h2 className="font-serif text-4xl md:text-5xl font-bold mt-3 mb-5 text-neutral-900">
                Why Choose Headtouch?
              </h2>
              <div className="gold-divider mb-6" />
              <p className="text-neutral-600 leading-relaxed mb-8 text-lg">
                We are committed to providing exceptional salon services in a warm, welcoming
                environment. Your satisfaction is our priority.
              </p>
              <ul className="space-y-4">
                {WHY_CHOOSE.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-neutral-800 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gold/10 rounded-3xl rotate-2" />
              <img
                src={IMAGES.whyChoose}
                alt="Headtouch salon styling area"
                className="relative rounded-2xl shadow-xl w-full h-[460px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg px-6 py-4 border border-gold/20 hidden md:block">
                <div className="flex items-center gap-3">
                  <Star className="w-8 h-8 fill-gold text-gold" />
                  <div>
                    <div className="font-bold text-2xl text-neutral-900">5.0</div>
                    <div className="text-xs text-neutral-500">Customer Rating</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28 bg-gold-soft/30">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-gold font-medium text-sm tracking-[0.25em] uppercase">Testimonials</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold mt-3 mb-5 text-neutral-900">
              What Our Clients Say
            </h2>
            <div className="gold-divider mx-auto mb-6" />
            <p className="text-neutral-600">Read reviews from our satisfied customers</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 6).map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-7 border border-gold/10 shadow-sm hover:shadow-md btn-transition">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-neutral-700 leading-relaxed mb-5 italic">{t.text}</p>
                <div className="border-t border-neutral-100 pt-4">
                  <div className="font-semibold text-neutral-900">&mdash; {t.name}</div>
                  <div className="text-sm text-gold mt-0.5">{t.service}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOK CTA */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-gold to-gold-dark text-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-5">
            Book Your Appointment Today
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Experience professional salon services in a relaxing environment. Walk-ins welcome!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={telLink}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gold-dark rounded-full font-semibold shadow-lg btn-transition hover:shadow-xl hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" />
              Call {SALON.phone}
            </a>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-whatsapp text-white rounded-full font-semibold shadow-lg hover:bg-whatsapp-dark btn-transition hover:shadow-xl hover:-translate-y-0.5"
            >
              <MessageCircle className="w-5 h-5 fill-white" />
              Book on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
